import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";

export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const queryKey = useMemo(() => ["persistedState", key], [key]);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey,
    queryFn: () => readLocalStorage<T>(key),
  });

  const set = useCallback(
    (value: SetStateAction<T>) => {
      const data = queryClient.setQueryData(queryKey, value);
      if (typeof localStorage !== "undefined")
        localStorage.setItem(key, JSON.stringify(data));
    },
    [key, queryKey, queryClient]
  );

  return [data ?? defaultValue, set];
}

export function readLocalStorage<T>(key: string) {
  try {
    if (typeof localStorage === "undefined") return null;
    const value = localStorage.getItem(key);
    if (value) return JSON.parse(value) as T;
    return null;
  } catch {
    // Ignore if localStorage could not be read
    return null;
  }
}
